import styled, { css } from 'styled-components';
import { FC } from 'react';
import Typography from '../../styles/Typography';
import { StyledWarehouseProductCardProps } from './types';
import { TypographyProps } from '../../styles/Typography/types';

export type StockStatus = 'in' | 'low' | 'out';

/** 6 pastel bg colors cycling per card index — locked per spec */
export const PASTEL_BG_COLORS = [
  '#FCE4EC', '#E8F5E9', '#F3E5F5', '#FFF8E1', '#E0F7FA', '#FFEBEE',
] as const;

interface StyledThumbnailProps {
  $bgColor?: string;
}

export const StyledCardThumbnail = styled.div<StyledThumbnailProps>`
  position: relative;
  aspect-ratio: 5 / 4;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $bgColor }) => $bgColor ?? '#F0F4F8'};
  /* negative margin to bleed to card edges */
  margin: calc(var(--s-3) * -1) calc(var(--s-3) * -1) var(--s-3);
  border-radius: var(--r-lg) var(--r-lg) 0 0;
  overflow: hidden;

  svg {
    width: 2rem;
    height: 2rem;
    color: rgba(0, 0, 0, 0.25);
    opacity: 0.9;
  }
`;

export type BadgeTone = 'success' | 'warning' | 'danger';

interface StyledStatusBadgeProps {
  tone: BadgeTone;
}

const badgeColors: Record<BadgeTone, { bg: string; ink: string; dot: string }> = {
  success: { bg: 'var(--success-soft)',  ink: 'var(--success-ink)',  dot: 'var(--success)' },
  warning: { bg: 'var(--warning-soft)',  ink: 'var(--warning-ink)',  dot: 'var(--warning)' },
  danger:  { bg: 'var(--danger-soft)',   ink: 'var(--danger-ink)',   dot: 'var(--danger)'  },
};

export const StyledStatusBadge = styled.span<StyledStatusBadgeProps>`
  position: absolute;
  top: var(--s-2);
  left: var(--s-2);
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  padding: 3px var(--s-2);
  border-radius: 999px;
  font-size: 0.6875rem;
  font-weight: var(--weight-semibold);
  letter-spacing: 0.02em;
  background-color: ${({ tone }) => badgeColors[tone].bg};
  color: ${({ tone }) => badgeColors[tone].ink};

  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ tone }) => badgeColors[tone].dot};
    flex-shrink: 0;
  }
`;

export const StyledWarehouseProductCard = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`
          display: grid;
          grid-template-columns: 56px minmax(0, 2fr) 72px 80px 80px 96px 100px auto;
          grid-template-areas: 'image name ordered remaining damaged created price action';
          align-items: center;
          column-gap: 1.25rem;
          padding: 0.5rem 0.75rem;
          min-height: 3.5rem;
          box-shadow: var(--sh-1);
        `
      : css`
          display: flex;
          flex-direction: column;
          padding: var(--s-3);
          box-shadow: var(--sh-1);
          overflow: hidden;
        `};
  width: 100%;
  cursor: pointer;
  background-color: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
  transition: border-color var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out);

  &:hover {
    border-color: var(--line-2);
    box-shadow: var(--sh-2);
  }
`;

export const StyledWarehouseProductCardImageWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal &&
    css`
      grid-area: image;
    `}
`;

export const StyledWarehouseProductLabel = styled(Typography).attrs({
  forwardedAs: 'span',
  variant: 'caption',
})`
  display: block;
  color: var(--muted);
  font-weight: var(--weight-medium);
  margin-bottom: 0.5rem;
  margin-top: 0.5rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const StyledStatRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
`;

export const StyledStatDot = styled.span<{ tone: 'good' | 'warn' | 'muted' }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ tone }) =>
    tone === 'good'
      ? 'var(--brand-green)'
      : tone === 'warn'
      ? 'var(--danger)'
      : 'var(--muted-3)'};
`;

export const StyledWarehouseProductNameLabel = styled<FC<TypographyProps>>(
  Typography
).attrs({
  forwardedAs: 'span',
  variant: 'caption',
})<StyledWarehouseProductCardProps>`
  display: block;
  color: var(--ink);
  font-weight: var(--weight-medium);
  font-size: 0.9375rem;
  margin-bottom: 0.5rem;
  margin-top: 0.25rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`width: 80px;`
      : css`width: 100%;`}
`;

export const StyledWarehouseCategoryNameLabel = styled<FC<TypographyProps>>(
  Typography
).attrs({
  forwardedAs: 'span',
  variant: 'caption',
})<StyledWarehouseProductCardProps>`
  display: inline-block;
  color: var(--ink-2);
  font-weight: var(--weight-medium);
  font-size: 0.6875rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  padding: 0.1875rem 0.5rem;
  background-color: rgba(9, 15, 56, 0.05);
  border-radius: 999px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-top: 0.25rem;
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`max-width: 80px;`
      : css`max-width: 150px;`}
`;

export const StyledWarehouseProductDateLabel = styled(Typography).attrs({
  forwardedAs: 'span',
  variant: 'caption',
})`
  color: var(--muted-2);
  font-size: 0.75rem;
  white-space: nowrap;
  margin-top: 0.25rem;
`;

export const StyledWarehouseProductCardNameWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`grid-area: name;`
      : css`order: 1; margin-bottom: 0.625rem;`}
`;

export const StyledWarehouseProductCardOrderedWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal && css`grid-area: ordered;`}
`;

export const StyledWarehouseProductCardRemainingUnitsWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal && css`grid-area: remaining;`}
`;

export const StyledWarehouseProductCardDamagedUnitsWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal && css`grid-area: damaged;`}
`;

export const StyledWarehouseProductCardDateCreatedWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`grid-area: created;`
      : css`grid-column: span 2;`}
`;

export const StyledWarehouseProductCardInfoGroup = styled.div<StyledWarehouseProductCardProps>`
  display: grid;
  grid-template-columns: repeat(3, auto);
  grid-column-gap: 1.5rem;
  margin-bottom: 1.625rem;
  order: 3;
`;

export const StyledWarehouseProductPrice = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const StyledWarehouseProductCardPriceWrapper = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`grid-area: price;`
      : css`order: 2; margin-bottom: 1rem;`}
`;

export const StyledWarehouseProductCurrency = styled(Typography).attrs({
  forwardedAs: 'span',
  variant: 'currencyBold',
})`
  text-transform: uppercase;
`;

/** Compact inline stats row shown in grid-card body (Ordered · Remaining · Damaged) */
export const StyledCardStatsRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--s-4);
  margin-bottom: var(--s-3);
  flex-wrap: wrap;
`;

export const StyledCardStatItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--muted);
`;

export const StyledWarehouseProductCardAction = styled.div<StyledWarehouseProductCardProps>`
  ${({ isHorizontal }) =>
    isHorizontal
      ? css`grid-area: action;`
      : css`
          order: 5;
          display: flex;
          align-items: center;
          gap: var(--s-2);
          border-top: 1px solid var(--line);
          padding-top: var(--s-3);
          margin-top: var(--s-2);

          > button:last-child,
          > a:last-child {
            flex: 1;
          }
        `}
  ${({ isHorizontal }) =>
    isHorizontal &&
    css`
      display: grid;
      grid-template-columns: repeat(2, auto);
      grid-column-gap: 0.625rem;
    `}
`;
